import React, { useLayoutEffect, useState } from 'react';

import { DIAGRAM_REFRESH_DEBOUNCE_MS } from '../../constants';
import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import {
  SankeyRenderContext,
  SankeyRenderState,
} from '../../contexts/sankeyRenderContext';
import { buildSankeyDiagram } from '../../diagram';
import { useSankeySyncer } from '../../utils/form';

type Props = {
  children: React.ReactNode;
};

/*
 * This hook is used to keep the diagram in sync with the Sankey form data.
 * It is called any time the form is updated without being smart about whether
 * the change will actually affect the diagram. This is intentional so that we
 * don't have to think about it when writing the form and to keep the diagram
 * logic trully decoupled from the form logic. We will optimize this again
 * once the Sankey diagram container is refactored to use React.
 */
const SankeyRenderProvider: React.FC<Props> = ({ children }) => {
  const { form, isFetching } = useSankeyFormContext();

  const [hasModified, setHasModified] = useState(false);
  const [renderState, setRenderState] = useState<SankeyRenderState>();

  // Trigger form validation and sync the diagram only if the form is valid.
  const syncDiagram = () => {
    form.handleSubmit(() => {
      setRenderState(buildSankeyDiagram(form));
      setHasModified(true);
    })();
  };

  // Render the diagram whenever it has been (re)fetched. This includes the
  // initial diagram load and any subsequent diagram fetches due to browser
  // tab getting new focus. Autosaves flow in the other direction and don't
  // cause diagrams to be refetched.
  useLayoutEffect(() => {
    if (!isFetching) {
      setRenderState(buildSankeyDiagram(form));
    }
  }, [isFetching]);

  useSankeySyncer({
    callback: syncDiagram,
    debounceMs: DIAGRAM_REFRESH_DEBOUNCE_MS,
  });

  if (!renderState) {
    return <>{children}</>;
  }

  return (
    <SankeyRenderContext.Provider value={{ ...renderState, hasModified }}>
      {children}
    </SankeyRenderContext.Provider>
  );
};

export default SankeyRenderProvider;
